'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Shape = 'original' | 'portrait' | 'square';

// Instagram's profile grid shows 3:4 portrait thumbnails (since January 2025); square
// tiles suit older grids and other platforms.
const RATIO: Record<Exclude<Shape, 'original'>, number> = { portrait: 3 / 4, square: 1 };

export default function BrowserImageSplitter({
  config,
}: {
  config?: { toolSlug?: string; heading?: string; variant?: string };
}) {
  const instagram = config?.variant === 'instagram-grid';
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [name, setName] = useState('image');
  const [cols, setCols] = useState(3);
  const [rows, setRows] = useState(instagram ? 3 : 2);
  const [shape, setShape] = useState<Shape>(instagram ? 'portrait' : 'original');
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  function load(file: File | undefined) {
    if (!file) return;
    setName(file.name.replace(/\.[^.]+$/, '') || 'image');
    const img = new Image();
    img.onload = () => setImage(img);
    img.src = URL.createObjectURL(file);
  }

  // The source area to split: the whole image, or a centred crop whose aspect makes every
  // tile the chosen shape.
  function area(img: HTMLImageElement) {
    const W = img.naturalWidth;
    const H = img.naturalHeight;
    if (shape === 'original') return { x: 0, y: 0, w: W, h: H };
    const target = (cols * RATIO[shape]) / rows;
    return W / H > target
      ? { x: (W - H * target) / 2, y: 0, w: H * target, h: H }
      : { x: 0, y: (H - W / target) / 2, w: W, h: W / target };
  }

  useEffect(() => {
    if (!image || !canvasRef.current) return;
    const c = canvasRef.current;
    const a = area(image);
    const scale = Math.min(1, 900 / a.w);
    c.width = Math.round(a.w * scale);
    c.height = Math.round(a.h * scale);
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(image, a.x, a.y, a.w, a.h, 0, 0, c.width, c.height);
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 2;
    for (let i = 1; i < cols; i += 1) {
      ctx.beginPath();
      ctx.moveTo((c.width * i) / cols, 0);
      ctx.lineTo((c.width * i) / cols, c.height);
      ctx.stroke();
    }
    for (let j = 1; j < rows; j += 1) {
      ctx.beginPath();
      ctx.moveTo(0, (c.height * j) / rows);
      ctx.lineTo(c.width, (c.height * j) / rows);
      ctx.stroke();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image, cols, rows, shape]);

  async function download() {
    if (!image) return;
    setBusy(true);
    void consumeToolUsage(config?.toolSlug ?? 'image-splitter', '').catch(() => undefined);
    try {
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      const a = area(image);
      const tw = a.w / cols;
      const th = a.h / rows;
      const tile = document.createElement('canvas');
      tile.width = Math.round(tw);
      tile.height = Math.round(th);
      const ctx = tile.getContext('2d');
      if (!ctx) return;
      for (let r = 0; r < rows; r += 1) {
        for (let col = 0; col < cols; col += 1) {
          ctx.clearRect(0, 0, tile.width, tile.height);
          ctx.drawImage(image, a.x + col * tw, a.y + r * th, tw, th, 0, 0, tile.width, tile.height);
          const blob = await new Promise<Blob | null>((res) => tile.toBlob(res, 'image/png'));
          // Instagram posts the last upload first, so number tiles in upload order:
          // bottom-right first, top-left last.
          const n = instagram ? rows * cols - (r * cols + col) : r * cols + col + 1;
          if (blob) zip.file(`${name}-${String(n).padStart(2, '0')}.png`, blob);
        }
      }
      const out = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(out);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name}-${cols}x${rows}.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{instagram ? 'Instagram' : 'Split'}</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Image splitter'}</h2>
        <p className="tool-live-subtitle">
          Cut one image into a grid of tiles and download them as a ZIP. Runs in your browser; the
          image is never uploaded.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose an image</span>
            <input accept="image/*" onChange={(e) => load(e.target.files?.[0])} type="file" />
          </label>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <label className="tool-live-field">
              <span>Columns</span>
              <select onChange={(e) => setCols(Number(e.target.value))} value={cols}>
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>Rows</span>
              <select onChange={(e) => setRows(Number(e.target.value))} value={rows}>
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="tool-live-field tool-live-field-full">
            <span>Tile shape</span>
            <select onChange={(e) => setShape(e.target.value as Shape)} value={shape}>
              <option value="original">Keep the whole image</option>
              <option value="portrait">Instagram grid 3:4 (crops to fit)</option>
              <option value="square">Square 1:1 (crops to fit)</option>
            </select>
          </label>
          <div className="tool-live-actions">
            <button className="button" disabled={!image || busy} onClick={download} type="button">
              {busy ? 'Preparing…' : `Download ${cols * rows} tiles (ZIP)`}
            </button>
          </div>
          {instagram ? (
            <p className="tool-live-panel-note">
              Files are numbered in posting order: upload 01 first and the last file last, so the
              grid assembles correctly on your profile.
            </p>
          ) : null}
        </section>
        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: image ? 'block' : 'none',
            }}
          />
          {image ? null : <p className="tool-live-panel-note">Pick an image to see the grid.</p>}
        </section>
      </div>
    </div>
  );
}
